import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';

@Injectable({
  providedIn: 'root',
})
export class StatisticsAggregator {

  public aggregateCount(
    stats: { [date: string]: any[] },
    aggregateBy: 'day' | 'week' | 'month',
  ): { labels: string[]; values: number[] } {

    const count =
      (aggregate: number, values: any[]) => {
        return aggregate + values.length;
      };

    return this.aggregateGroupedData<any[], number>(
      stats,
      aggregateBy,
      //
      (group: string, aggregateBy: string) => this.generateLabel(group, aggregateBy),
      (labels: string[], aggregateBy: string) => this.sortLabels(labels, aggregateBy),
      //
      () => 0,
      (aggregate, value) => count(aggregate, value),
      (aggregate) => aggregate,
    );
  }

  public aggregateCountDistinct(
    stats: { [date: string]: any[] },
    aggregateBy: 'day' | 'week' | 'month',
  ): { labels: string[]; values: number[] } {

    const collectUniqueByValue =
      (aggregate: any[], values: any[]) => {
        const combined = [...aggregate, ...values];
        return this.uniqueByValue(combined);
      };

    return this.aggregateGroupedData<any[], any[]>(
      stats,
      aggregateBy,
      //
      (group: string, aggregateBy: string) => this.generateLabel(group, aggregateBy),
      (labels: string[], aggregateBy: string) => this.sortLabels(labels, aggregateBy),
      //
      () => [],
      (aggregate, values) => collectUniqueByValue(aggregate, values),
      (aggregate) => aggregate.length,
    );
  }

  public aggregateDuration(
    stats: { [date: string]: number[] },
    aggregateBy: 'day' | 'week' | 'month',
  ): { labels: string[]; values: number[] } {

    const sumDurations =
      (aggregate: number, durations: number[]) => {
        return (
          aggregate +
          durations
            .filter(val => typeof val === 'number' && !isNaN(val))
            .reduce((acc, val) => acc + val, 0)
        );
      };

    return this.aggregateGroupedData<number[], number>(
      stats,
      aggregateBy,
      //
      (group: string, aggregateBy: string) => this.generateLabel(group, aggregateBy),
      (labels: string[], aggregateBy: string) => this.sortLabels(labels, aggregateBy),
      //
      () => 0,
      (aggregate, durations) => sumDurations(aggregate, durations),
      (aggregate) => aggregate,
    );
  }

  private aggregateGroupedData<T, U>(
    data: { [group: string]: T },
    aggregateBy: string,
    labelGenerator: (group: string, aggregateBy: string) => string,
    labelSorter: (labels: string[], aggregateBy: string) => string[],
    initializer: () => U,
    aggregator: (aggregate: U, value: T) => U,
    finalizer: (aggregate: U) => number,
  ): { labels: string[]; values: number[] } {

    const aggregates: { [label: string]: U } = {};

    for (const group in data) {

      const label = labelGenerator(group, aggregateBy);

      if (!aggregates[label]) {
        aggregates[label] = initializer();
      }

      aggregates[label] = aggregator(aggregates[label], data[group]);
    }

    const labels: string[] = [];
    const values: number[] = [];

    labelSorter(Object.keys(aggregates), aggregateBy)
      .forEach((label: string) => {

        labels.push(label);
        values.push(finalizer(aggregates[label]));
      });

    return {
      labels,
      values,
    };
  }

  private generateLabel(
    group: string,
    aggregateBy: string,
  ): string {

    const dt = DateTime.fromISO(group);

    switch (aggregateBy) {

      case 'week':
        return `${dt.weekYear}-W${dt.weekNumber}`;

      case 'month':
        return dt.toFormat('yyyy-MM');

      case 'day':
      default:
        return group;
    }
  }

  private sortLabels(
    labels: string[],
    aggregateBy: string,
  ): string[] {

    return labels.sort((a, b) => {

      if (aggregateBy === 'day') {
        return new Date(a).getTime() - new Date(b).getTime();

      } else if (aggregateBy === 'week') {

        const [yearA, weekA] = a.split('-W').map(Number);
        const [yearB, weekB] = b.split('-W').map(Number);

        if (yearA !== yearB) {
          return yearA - yearB;
        } else {
          return weekA - weekB;
        }

      } else if (aggregateBy === 'month') {
        return new Date(a + '-01').getTime() - new Date(b + '-01').getTime();
      }

      return 0;
    });
  }

  private uniqueByValue<T>(
    values: T[],
  ): T[] {

    const uniqueMap = new Map(values.map(value => [JSON.stringify(value), value]));

    return Array.from(uniqueMap.values());
  }

}
